import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Plane, BedDouble, QrCode, Bus, Luggage, ArrowRight, CalendarDays, Radio,
} from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import EventPicker from '../../components/EventPicker';
import {
  Card, Stat, Loader, EmptyState, SectionTitle, Badge, stagger,
} from '../../components/UI';
import { fmtNumber, fmtDate, pct } from '../../utils/format';

/* On-site work, in the order a staff member usually reaches for it. */
const ACTIONS = [
  { to: '/staff/attendance', label: 'Scan & mark attendance', icon: QrCode },
  { to: '/staff/transport', label: 'Coaches & boarding', icon: Bus },
  { to: '/staff/luggage', label: 'Luggage desk', icon: Luggage },
  { to: '/staff/rooms', label: 'Room allocation', icon: BedDouble },
  { to: '/staff/participants', label: 'Participant list', icon: Users },
  { to: '/staff/flights', label: 'Travel readiness', icon: Plane },
];

export default function StaffDashboard() {
  const { user } = useAuth();
  const { eventId, event, events, loading: eventsLoading } = useEvents();

  const { data: dash, loading } = useFetch(
    eventId ? `/events/${eventId}/dashboard` : null, {}, { skip: !eventId },
  );

  if (eventsLoading) return <Loader full />;

  if (!events.length) {
    return (
      <EmptyState
        message="No events assigned yet"
        hint="Your coordinator will assign you to an event. It will appear here as soon as they do."
        icon={CalendarDays}
      />
    );
  }

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <Card padding={24} style={{ marginBottom: 20, overflow: 'hidden' }}>
        <motion.div
          animate={{ opacity: [0.35, 0.65, 0.35] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', top: -80, right: -40, width: 280, height: 280,
            background: 'radial-gradient(circle, rgba(239,212,138,0.11), transparent 70%)',
            filter: 'blur(38px)', pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <p style={{ fontSize: 11.5, color: 'var(--gold-500)', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600 }}>
              On duty · {user?.name?.split(' ')[0]}
            </p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 27, marginTop: 6, fontWeight: 600 }}>
              {event?.title || 'Select an event'}
            </h2>
            {event && (
              <p style={{ color: 'var(--text-mid)', fontSize: 13, marginTop: 5 }}>
                {fmtDate(event.startDate)} – {fmtDate(event.endDate)} · {event.venue?.name || event.venue?.city || 'Location TBA'}
              </p>
            )}
          </div>
          <EventPicker />
        </div>
      </Card>

      {loading ? <Loader label="Loading event summary" /> : dash && (
        <>
          {dash.sessions.live > 0 && (
            <Card padding={16} style={{
              marginBottom: 20, borderColor: 'rgba(74,222,128,0.28)', background: 'rgba(74,222,128,0.05)',
              display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap',
            }}>
              <Radio size={19} color="var(--ok)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <p style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {dash.sessions.live} session{dash.sessions.live === 1 ? '' : 's'} open for scanning right now
                </p>
                <p style={{ fontSize: 12.5, color: 'var(--text-mid)', marginTop: 3 }}>
                  Attendance is only accepted while a session's window is open.
                </p>
              </div>
              <Link to="/staff/attendance"><Badge tone="gold">Go to attendance</Badge></Link>
            </Card>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(215px, 1fr))', gap: 14, marginBottom: 20 }}>
            <Stat label="Participants" value={fmtNumber(dash.participants.total)} icon={Users} tone="info"
              sub={`${dash.participants.vips} VIP`} />
            <Stat label="Checked In" value={fmtNumber(dash.participants.checkedIn)} icon={QrCode} tone="success"
              progress={pct(dash.participants.checkedIn, dash.participants.total)}
              sub={`${pct(dash.participants.checkedIn, dash.participants.total)}% arrived`} />
            <Stat label="Boarded" value={fmtNumber(dash.transport.boarded)} icon={Bus} tone="gold"
              sub={`${dash.transport.busesTotal} coach${dash.transport.busesTotal === 1 ? '' : 'es'}`} />
            <Stat label="Rooms Occupied" value={`${dash.stay.roomsOccupied}/${dash.stay.roomsTotal}`} icon={BedDouble} tone="warning"
              progress={pct(dash.stay.roomsOccupied, dash.stay.roomsTotal)}
              sub={`${dash.stay.roomsPending} still to check in`} />
            <Stat label="Docs Pending" value={fmtNumber(dash.travel.docsPending)} icon={Plane} tone="danger"
              sub={`${dash.travel.ticketsIssued} tickets issued`} />
          </div>

          <Card padding={22}>
            <SectionTitle title="Run the event" subtitle="Everything you can manage for this event" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
              {ACTIONS.map((a, i) => (
                <motion.div key={a.to} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={a.to}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px',
                      border: '1px solid var(--border)', borderRadius: 'var(--r)',
                      background: 'var(--ink-800)', fontSize: 13, transition: 'border-color 0.2s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(239,212,138,0.32)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <a.icon size={15} color="var(--gold-500)" />
                      <span style={{ flex: 1 }}>{a.label}</span>
                      <ArrowRight size={13} color="var(--text-lo)" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </Card>
        </>
      )}
    </motion.div>
  );
}
